import { useNavigate } from 'react-router-dom'
import { useAudio } from './AudioContext'
import { Play, Pause, X } from 'lucide-react'
import { useTheme } from '@/shared/contexts/ThemeContext'

type MiniAudioPlayerProps = {
  /**
   * Angedockt sitzt die Leiste über dem Player der Leseansicht, weil dort ein anderer
   * Artikel gelesen wird als der, der läuft. Dann entscheidet der Player, wann sie zu
   * sehen ist - nicht `showMiniPlayer` - und sie bringt ihre eigene Lage nicht mit.
   */
  docked?: boolean
}

export function MiniAudioPlayer({ docked = false }: MiniAudioPlayerProps) {
  const { theme } = useTheme()
  const navigate = useNavigate()
  const {
    isPlaying,
    currentArticle,
    articlesWithAudio,
    currentTime,
    duration,
    chapterSlug,
    chapterTitle,
    showMiniPlayer,
    togglePlay,
    saveProgressAndClose,
    onMiniPlayerClick,
    shouldSaveProgress,
  } = useAudio()

  if ((!showMiniPlayer && !docked) || !currentArticle) return null

  // Ein Artikel besteht aus mehreren Audio-Einheiten (Haupttext plus Abschnitte). Der
  // Balken zeigt den Stand im ganzen Artikel, nicht in der laufenden Datei - sonst
  // spränge er bei jedem Abschnittswechsel zurück auf null.
  //
  // `duration || 60` ist derselbe Notnagel wie in der Fortschrittsleiste der
  // Leseansicht: Einheiten ohne hinterlegte Dauer bekommen einen Platzhalter, damit
  // die Leiste nicht zusammenfällt.
  const articleUnits = articlesWithAudio.filter((u) => u.slug === currentArticle.slug)
  const currentUnitIndex = articleUnits.findIndex((u) => u.unitKey === currentArticle.unitKey)
  const articleDuration = articleUnits.reduce((sum, u) => sum + (u.duration || 60), 0)
  const elapsedBeforeUnit = articleUnits
    .slice(0, Math.max(currentUnitIndex, 0))
    .reduce((sum, u) => sum + (u.duration || 60), 0)
  const articleElapsed = elapsedBeforeUnit + currentTime
  // Kennt der Context die Einheiten (noch) nicht, zeigt der Balken wenigstens die
  // laufende Datei statt stur auf null zu stehen.
  const rawRatio =
    currentUnitIndex >= 0 && articleDuration > 0
      ? articleElapsed / articleDuration
      : duration > 0
        ? currentTime / duration
        : 0
  const articleRatio = Math.min(Math.max(rawRatio, 0), 1)

  /**
   * Ein Tipp auf die Karte zeigt den laufenden Artikel wieder.
   *
   * Steht die Ansicht, die ihn zeigen kann, noch (die Kapitelseite oder das Kurzkapitel
   * im Layout), öffnet sie sich selbst wieder - ohne Navigation, damit die Seite
   * darunter samt Scrollposition stehen bleibt. Von überall sonst führt der Weg über
   * die Kapitelroute, wie beim Weiterlesen-Knopf in "Meine Titel": `read=true` fährt
   * die Leseansicht hoch, ohne das laufende Audio anzufassen, `start` benennt den
   * Artikel. Ohne diesen Weg blieb der Tipp wirkungslos, sobald man die Seite verlassen
   * hatte, auf der das Kapitel geöffnet wurde.
   */
  const handleBoxClick = () => {
    // Angedockt zeigt der Rückruf auf die Ansicht, die gerade offen liegt - und die
    // gehört einem anderen Kapitel. Hierher führt dann nur die Route des laufenden.
    if (onMiniPlayerClick && !docked) {
      onMiniPlayerClick()
      return
    }
    if (!chapterSlug) return
    const params = new URLSearchParams({ read: 'true', start: currentArticle.slug })
    // Der Wiedereinstiegspunkt darf sich nur verschieben, wenn das Kapitel aus "Meine
    // Titel" geöffnet wurde. Dieser Weg führt in dasselbe Kapitel zurück, also muss die
    // Herkunft mit - sonst hörte das Mitschreiben beim Wiederöffnen auf.
    if (shouldSaveProgress) params.set('from', 'meineTitel')
    navigate(`/user/chapter/${chapterSlug}?${params}`)
  }

  return (
    <div
      className={
        docked
          ? 'animate-fadeIn'
          : 'fixed bottom-[5.5rem] sm:bottom-[4.5rem] left-4 right-4 z-40 animate-fadeIn'
      }
    >
      <div
        className="rounded-2xl text-white overflow-hidden shadow-lg"
        style={{ backgroundColor: theme === 'dark' ? 'hsl(356, 21%, 38%)' : 'hsl(24, 17%, 53%)' }}
      >
        <div className="flex items-center justify-between p-4">
          <button
            onClick={handleBoxClick}
            className="flex-1 min-w-0 mr-3 text-left cursor-pointer hover:opacity-90 transition"
          >
            <h3
              className="text-sm font-semibold truncate"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
            >
              {chapterTitle || 'Kapitel'}
            </h3>
            <p className="text-xs text-white/80 truncate mt-0.5">{currentArticle.title}</p>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={togglePlay}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 transition"
              aria-label={isPlaying ? 'Pause' : 'Abspielen'}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" fill="currentColor" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" fill="currentColor" />
              )}
            </button>
            <button
              onClick={saveProgressAndClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 transition"
              aria-label="Schließen"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Sitzt auf der Unterkante der Karte; `overflow-hidden` am Container schneidet
            sie an den Rundungen sauber ab. Bewusst ohne Klickziel - zum Spulen tippt
            man die Karte an und landet im großen Player. */}
        <div
          className="h-1 w-full bg-white/20"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(articleRatio * 100)}
          aria-label={`Fortschritt in ${currentArticle.title}`}
        >
          <div className="h-full bg-white/80" style={{ width: `${articleRatio * 100}%` }} />
        </div>
      </div>
    </div>
  )
}
